import { Key, Copy, Check } from 'lucide-react';
import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { cn } from '../utils/cn';

interface RedisKeyInfoProps {
  redisKey: string | null | undefined;
  className?: string;
}

const TooltipContent = ({ redisKey, isCopied, style }: { redisKey: string, isCopied: boolean, style: React.CSSProperties }) => {
    return (
        <div
            className={cn(
                "fixed w-max max-w-xs px-3 py-2 bg-dark-900 border border-glass-200/20 rounded-lg text-xs text-white z-[9999] transition-opacity duration-200"
            )}
            style={{ ...style, boxShadow: 'var(--shadow-popover)' }}
            role="tooltip"
        >
            {isCopied ? (
                <div className="flex items-center gap-2 text-green-400">
                    <Check className="w-4 h-4" />
                    <span>Copied!</span>
                </div>
            ) : (
                <div className="flex flex-col items-center gap-1">
                    <span className="font-mono">{redisKey}</span>
                    <span className="text-[11px] text-glass-400 flex items-center gap-1">
                        <Copy className="w-3 h-3" />
                        <span>Click icon to copy</span>
                    </span>
                </div>
            )}
        </div>
    );
};


export const RedisKeyInfo: React.FC<RedisKeyInfoProps> = ({ redisKey, className }) => {
  if (!redisKey) {
    return null;
  }

  const [isCopied, setIsCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<{top: number, left: number, placement: 'top' | 'bottom'}>({ top: 0, left: 0, placement: 'top' });
  const iconRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const tooltipHeight = 60; // Estimated height of the tooltip
      
      let top: number;
      let placement: 'top' | 'bottom';

      if (rect.top < tooltipHeight + 20) { // If too close to top
        top = rect.bottom + 8; // Position below
        placement = 'bottom';
      } else {
        top = rect.top - 8; // Position above
        placement = 'top';
      }

      setPosition({
        top: top,
        left: rect.left + rect.width / 2,
        placement: placement,
      });
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isCopied) {
      setIsVisible(false);
    }
  };
  
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (redisKey && !isCopied) {
      navigator.clipboard.writeText(redisKey).then(() => {
        setIsCopied(true);
        // Ensure tooltip is visible and correctly positioned for feedback
        handleMouseEnter();
        
        setTimeout(() => {
            setIsCopied(false);
            // Hide tooltip only if mouse is no longer over the icon
            if (iconRef.current && !iconRef.current.matches(':hover')) {
                setIsVisible(false);
            }
        }, 2000);
      });
    }
  };

  const tooltipStyle: React.CSSProperties = {
      top: `${position.top}px`,
      left: `${position.left}px`,
      transform: position.placement === 'top' ? 'translate(-50%, -100%)' : 'translateX(-50%)',
      opacity: isVisible ? 1 : 0,
      pointerEvents: isVisible ? 'auto' : 'none',
  };

  return (
    <>
      <div
        ref={iconRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleCopy}
        role="button"
        aria-label="Copy Redis key"
        className={cn('inline-flex items-center justify-center w-6 h-6 rounded-md cursor-pointer text-glass-400/60 hover:text-accent-300 hover:bg-glass-200/10 transition-colors', className)}
      >
        <Key className="w-3.5 h-3.5" />
      </div>
      {typeof document !== 'undefined' && ReactDOM.createPortal(
        <TooltipContent
            redisKey={redisKey}
            isCopied={isCopied}
            style={tooltipStyle}
        />,
        document.body
      )}
    </>
  );
};
